import React, { useMemo } from 'react';
import * as yup from 'yup';
import { SchemaOf } from 'yup';
import {
    DSection,
    RhFormWrapper,
    RhSingleAutocomplete,
} from '../../../../../dum';
import { useUpdateOrderQuotationStatusMutation } from '../../../../../../services';
import {
    FIELD_LABELS,
    shortLabel,
    transformToNumber,
} from '../../../../../../helpers';

interface IOrderQuotationStatusChange {
    order_quotation_status_id?: number | null;
}

interface IOrderQuotationStatusChangeFormProps {
    orderQuotationId: number;
    currentStatusId?: number | null;
    onSuccess: () => void;
}

// The statuses an admin may set by hand. Aceptada (3) is deliberately absent:
// it is reachable ONLY through acceptOrderQuotation (which also writes the
// catalog and the pedido), and updateOrderQuotationStatus refuses it. There is
// no getOrderQuotationStatuses query — the set is fixed and seeded.
const SETTABLE_STATUS_OPTIONS = [
    { id: 1, name: 'Borrador' },
    { id: 2, name: 'Enviada' },
    { id: 4, name: 'Rechazada' },
];

// Status-only form, separate from the quotation upsert. The upsert no longer
// accepts a status, so this admin-only mutation is the only way to move a
// quotation between the settable statuses.
export function OrderQuotationStatusChangeForm({
    orderQuotationId,
    currentStatusId,
    onSuccess,
}: IOrderQuotationStatusChangeFormProps) {
    const [mutation, { loading }] = useUpdateOrderQuotationStatusMutation();

    const initialValues = useMemo<IOrderQuotationStatusChange>(() => {
        return {
            order_quotation_status_id: currentStatusId || 1,
        };
    }, [currentStatusId]);

    const validationSchema: SchemaOf<IOrderQuotationStatusChange> = yup.object({
        order_quotation_status_id: yup
            .number()
            .transform(transformToNumber)
            .nullable()
            .required(`'Status' es obligatorio`),
    });

    return (
        <RhFormWrapper
            validationSchema={validationSchema}
            initialValues={initialValues}
            disabled={loading}
            onSubmit={async (formData) => {
                try {
                    await mutation({
                        variables: {
                            OrderQuotationId: orderQuotationId,
                            OrderQuotationStatusId:
                                formData.order_quotation_status_id!,
                        },
                    });
                    onSuccess();
                } catch (e) {
                    console.error(e);
                }
            }}
        >
            <DSection alwaysOpen>
                <RhSingleAutocomplete
                    itemText={'name'}
                    itemValue={'id'}
                    name={'order_quotation_status_id'}
                    label={shortLabel(FIELD_LABELS.order_request_status_id)}
                    options={SETTABLE_STATUS_OPTIONS}
                />
            </DSection>
        </RhFormWrapper>
    );
}
